import React, { useCallback, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import {
  Proposal,
  VoteChoice,
  DAO_EIP712_VOTE_TYPES,
  DAO_EIP712_DOMAIN_NAME,
  DAO_EIP712_DOMAIN_VERSION,
  DAO_EIP712_VERIFYING_CONTRACT
} from '@mprolab/sdk'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useLifetimeTokens } from 'state/global/hooks'
import { useCastVoteMutation } from 'state/services/indexNode/indexNode'
import { buildVotePayload, deriveDaoVoteDigest, DAO_VOTE_SIGNED_MESSAGE_VERSION } from 'utils/daoSignTypedData'
import useToast from 'hooks/useToast'
import { errorText } from 'utils/errorText'

const Bar = styled(Flex)`
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
`

interface VoteButtonRowProps {
  proposal: Proposal
  myVote?: { choice: VoteChoice; votingPowerSnapshot: number }
}

const VoteButtonRow: React.FC<VoteButtonRowProps> = ({ proposal, myVote }) => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { account, provider } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = account ? lifetimeTokens?.[account] : undefined
  const [castVote, { isLoading }] = useCastVoteMutation()
  const { toastSuccess, toastError } = useToast()
  const [submittingChoice, setSubmittingChoice] = useState<VoteChoice | null>(null)

  const onVote = useCallback(
    async (choice: VoteChoice) => {
      if (!account || !accessToken || !provider) {
        toastError(t('dao.signinFirst'))
        return
      }
      setSubmittingChoice(choice)
      try {
        const payload = buildVotePayload({
          proposalId: proposal.proposalId,
          choice,
          snapshotBlock: proposal.snapshotBlock,
          voterWallet: account
        })
        const signer = provider.getSigner(account)
        // ethers v5 signer typed-data signing helper.
        const signature = await (
          signer as unknown as {
            _signTypedData: (domain: unknown, types: unknown, value: unknown) => Promise<string>
          }
        )._signTypedData(
          {
            name: DAO_EIP712_DOMAIN_NAME,
            version: DAO_EIP712_DOMAIN_VERSION,
            chainId: proposal.snapshotChainId,
            verifyingContract: DAO_EIP712_VERIFYING_CONTRACT
          },
          DAO_EIP712_VOTE_TYPES,
          payload
        )
        const signedDigest = deriveDaoVoteDigest(payload, proposal.snapshotChainId)
        await castVote({
          account,
          accessToken,
          proposalId: proposal.proposalId,
          choice,
          signature,
          signedMessageVersion: DAO_VOTE_SIGNED_MESSAGE_VERSION,
          signedDigest,
          castAt: payload.castAt
        }).unwrap()
        toastSuccess(t('dao.voteRecorded'))
      } catch (e) {
        /*
         * A vote that lands twice is not a fault the voter caused, and the raw
         * conflict text reads like one. It can still happen — two tabs, or a
         * press that raced the refresh — so it gets said plainly rather than
         * relayed.
         */
        const conflict = (e as { status?: number })?.status === 409
        // String(thrown) is where "[object Object]" comes from: RTK Query
        // rejects with a plain object, and one voter read exactly that.
        const msg = errorText(e)
        toastError(conflict ? t('dao.alreadyVoted') : t('dao.voteFailed', { reason: msg }))
      } finally {
        setSubmittingChoice(null)
      }
    },
    [account, accessToken, provider, proposal, castVote, toastError, toastSuccess, t]
  )

  if (myVote) {
    return (
      <Bar>
        <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.cyan}>
          {t('dao.youVoted', {
            choice: t(`dao.choice.${myVote.choice}`),
            vp: myVote.votingPowerSnapshot.toLocaleString()
          })}
        </Text>
      </Bar>
    )
  }

  if (!account || !accessToken) {
    return (
      <Bar>
        <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
          {t('dao.connectAndVerifyToVote')}
        </Text>
      </Bar>
    )
  }

  return (
    <Bar>
      <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('dao.castYourVote')}</Text>
      <Button
        type="button"
        variant="primary"
        size="md"
        disabled={isLoading || submittingChoice !== null}
        onClick={() => void onVote(VoteChoice.YES)}
      >
        {t('dao.choice.yes')}
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="md"
        disabled={isLoading || submittingChoice !== null}
        onClick={() => void onVote(VoteChoice.NO)}
      >
        {t('dao.choice.no')}
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="md"
        disabled={isLoading || submittingChoice !== null}
        onClick={() => void onVote(VoteChoice.ABSTAIN)}
      >
        {t('dao.choice.abstain')}
      </Button>
      <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
        {t('dao.gasFreeVote')}
      </Text>
    </Bar>
  )
}

export default VoteButtonRow
